'use client';
import { useBreadcrumbs } from '@repo/ui/src/context/BreadCrumbProvider';
import React, { useEffect } from 'react';
import dynamic from 'next/dynamic';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import useWindowSize from '@repo/ui/src/hooks/useWindowSize';
import { classNames } from '@repo/ui/src/utils/classNames';
import TierTwoHeader from './_TierTwoHeading';
import HeroLogos from './_HeroLogos';
import { MediaType } from '@repo/ui/src/types/image';
import Button, { ButtonVariant } from '@repo/ui/src/components/Button';
import { Anchor } from '@repo/ui/src/components/Anchor';
import { LinkProps } from '@repo/ui/src/types/link';
import { HeaderVariationProps } from '@repo/ui/src/types/headerVariation';
import SecondaryCta from '@repo/ui/src/components/TierTwoHero/_SecondaryCta';
import { YoutubeLinkProps } from '@repo/ui/src/types/youtubeLink';
import { MetaProps } from '@repo/ui/src/types/meta';
import { useIsMobile } from '@repo/ui/src/utils/useMediaQuery';

const IMAGE_META_SCHEMA = 'http://bigcontent.io/cms/schema/v1/core#/definitions/image-link';
const VIDEO_META_SCHEMA = 'http://bigcontent.io/cms/schema/v1/core#/definitions/video-link';
const FULL_TO_THEATER = 1920;

const BackgroundVideo = dynamic(() => import('@repo/ui/src/components/BackgroundVideo'), {
  ssr: false,
});

const heroPadding = {
  none: 'md:tw-py-0',
  small: 'md:tw-py-20',
  medium: 'md:tw-py-28',
  large: 'md:tw-py-36',
};

interface Props {
  title?: string;
  description?: string;
  logoMaxHeight?: string;
  logoMaxWidth?: string;
  _testID?: string;
  opacityOverride?: boolean;
  logos?: Array<MediaType>;
  subtitle?: string;
  searchPlaceHolder?: string;
  showDivider?: boolean;
  searchBy?: string;
  navLinks?: Array<{
    link: LinkProps;
  }>;
  backgroundImage: MediaType;
  link?: LinkProps;
  cta?: Array<LinkProps>;
  headerVariation: HeaderVariationProps;
  opacityLayerClass?: string;
  fitToText?: boolean;
  youtubeLink?: YoutubeLinkProps;
  youtubestring?: string;
  _meta: MetaProps;
  children?: React.ReactNode;
  padding: 'small' | 'medium' | 'large' | 'none';
  videoOverlay?: boolean;
}

const TierTwoHero = ({
  backgroundImage,
  opacityLayerClass,
  _testID,
  opacityOverride = false,
  title,
  logos,
  subtitle,
  headerVariation,
  searchPlaceHolder,
  logoMaxHeight,
  logoMaxWidth,
  fitToText,
  description,
  link,
  navLinks,
  youtubeLink,
  youtubestring,
  _meta,
  cta,
  children,
  padding,
  videoOverlay,
}: Props) => {
  const { setDarkBackground } = useBreadcrumbs();
  const hasVideo = backgroundImage && backgroundImage._meta?.schema === VIDEO_META_SCHEMA;
  const subtitleAsDesc =
    _meta?.schema === 'https://traegergrills.com/data/contentTypes/generalHeroBannerWithText.json';

  const hasCta = (youtubeLink && youtubestring) || link?.displayName || cta;
  useEffect(() => {
    if (backgroundImage) {
      setDarkBackground(true);
    }
    return () => setDarkBackground(false);
  }, [backgroundImage, setDarkBackground]);

  const { width = 0 } = useWindowSize();
  const xsOnly = useIsMobile();

  let imgUrl;
  let isVideo;
  let vidWidth;

  if (backgroundImage?._meta?.schema === IMAGE_META_SCHEMA) {
    isVideo = false;
    imgUrl = buildImageSrc(backgroundImage, width, xsOnly ? width : 2, xsOnly ? 450 : 1);
  } else if (backgroundImage?._meta?.schema === VIDEO_META_SCHEMA) {
    isVideo = true;
    vidWidth = width > FULL_TO_THEATER ? FULL_TO_THEATER : width;
  }

  const givenHeightStyle = opacityLayerClass === 'given-height' && !isVideo;
  const blackOverlay = imgUrl || (isVideo && !opacityOverride);

  const maxHeight = logos || searchPlaceHolder;
  const hasNavLinks = navLinks?.[0]?.link?.displayName;

  return (
    <div
      className={classNames(
        'tw-relative tw-flex',
        !isVideo && 'tw-bg-no-repeat tw-bg-cover tw-bg-center',
        givenHeightStyle && 'tw-h-full tw-max-h-full',
      )}
      style={imgUrl ? { backgroundImage: `url('${imgUrl}')` } : undefined}
      data-test-id={_testID ? `hero_banner_${_testID}` : undefined}
      data-testid={_testID}
    >
      <div
        className={classNames(
          'tw-w-full tw-grid tw-grid-rows-1 tw-items-center tw-min-h-[400px] tw-overflow-y-hidden md:tw-max-h-screen',
          !opacityOverride && 'tw-z-20',
          backgroundImage && blackOverlay ? 'tw-bg-black tw-bg-opacity-60' : 'tw-bg-[#f6f4f3]',
          padding && !isVideo && heroPadding[padding],
          isVideo && 'tw-grid-areas-overlap',
          !givenHeightStyle && maxHeight && 'md:tw-min-h-[580px]',
        )}
      >
        {!children && (
          <div
            className={classNames(
              'tw-grid tw-h-full',
              isVideo && 'tw-grid-in-a tw-z-10',
              videoOverlay && 'tw-bg-black tw-bg-opacity-60',
            )}
            data-testid="video-overlay"
          >
            <div
              className={classNames(
                '!tw-text-center tw-flex tw-flex-col tw-justify-center tw-align-center tw-items-center tw-w-full sm:tw-max-w-paragraph tw-mx-auto tw-p-4 md:tw-py-20 tw-gap-6',
                fitToText && 'tw-pb-4 md:tw-pb-5 md:pt-4',
                backgroundImage ? 'tw-text-white' : 'tw-text-black',
              )}
            >
              <TierTwoHeader
                subtitleAsDesc={subtitleAsDesc}
                subtitle={subtitle}
                title={title}
                headerVariation={headerVariation}
                description={description}
                darkMode={!!backgroundImage}
              />
              {logos && (
                <HeroLogos
                  logos={logos}
                  logoMaxHeight={logoMaxHeight}
                  logoMaxWidth={logoMaxWidth}
                  hasVideo={hasVideo}
                />
              )}
              {hasCta && (
                <SecondaryCta
                  youtubeLink={youtubeLink}
                  youtubestring={youtubestring}
                  link={link}
                  cta={cta}
                />
              )}
              {hasNavLinks && (
                <div className="tw-flex tw-flex-wrap tw-justify-center tw-gap-5 sm:tw-flex-row sm:tw-gap-10">
                  {navLinks?.map((link, i) => (
                    <Button as={Anchor} variant={ButtonVariant.skewer} dark {...link?.link} key={i}>
                      {link?.link?.displayName}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        {children}
        {isVideo && vidWidth && (
          <BackgroundVideo
            video={backgroundImage}
            width={vidWidth}
            playButton
            className={classNames('[&_video]:tw-w-full tw-h-full', isVideo && 'tw-grid-in-a')}
            fillContainer
          />
        )}
      </div>
    </div>
  );
};

export default TierTwoHero;
